import { validateWhatsAppNumber } from "../../../shared/domain/whatsappNumber";

export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const PHONE_REGEX = /^\+?[1-9]\d{6,14}$/;
export const POSTAL_CODE_ES_REGEX = /^\d{5}$/;

/** @returns i18n key if invalid, undefined if valid */
export function validateName(value: string): string | undefined {
    if (!value.trim()) return "patient.nameRequired";
    return undefined;
}

/** @returns i18n key if invalid, undefined if valid */
export function validateLastName(value: string): string | undefined {
    if (!value.trim()) return "patient.lastNameRequired";
    return undefined;
}

/** @returns i18n key if invalid, undefined if valid */
export function validateEmail(value: string): string | undefined {
    if (!value.trim()) return "patient.emailRequired";
    if (!EMAIL_REGEX.test(value)) return "patient.emailInvalid";
    return undefined;
}

/** @returns i18n key if invalid, undefined if valid */
export function validatePhone(value: string): string | undefined {
    if (!value.trim()) return "patient.phoneRequired";
    if (!PHONE_REGEX.test(value)) return "patient.phoneInvalid";
    return undefined;
}

/** @returns i18n key if invalid, undefined if valid */
export function validateDateOfBirth(value: string): string | undefined {
    if (!value) return undefined;
    const date = new Date(value);
    if (isNaN(date.getTime()) || date >= new Date()) return "patient.dobInvalid";
    const ageYears = new Date().getFullYear() - date.getFullYear();
    if (ageYears > 120) return "patient.dobOutOfRange";
    return undefined;
}

/** @returns i18n key if invalid, undefined if valid */
export function validatePostalCode(value: string, country: string): string | undefined {
    if (!value) return undefined;
    if (country === "ES" && !POSTAL_CODE_ES_REGEX.test(value)) return "patient.postalCodeInvalidES";
    return undefined;
}

export interface PatientValidationData {
    first_name: string;
    last_name: string;
    contact_email: string;
    contact_phone: string;
    date_of_birth?: string;
    postal_code?: string;
    country?: string;
    /** Optional; a national number is completed with `country`. */
    whatsapp_phone?: string;
}

/** The fields with a rule; the rest take anything. */
export const VALIDATED_PATIENT_FIELDS = [
    "first_name",
    "last_name",
    "contact_email",
    "contact_phone",
    "date_of_birth",
    "whatsapp_phone",
    "postal_code",
] as const;

export type ValidatedPatientField = (typeof VALIDATED_PATIENT_FIELDS)[number];

/**
 * One field's rule, read against the rest of the form (a postal code and a
 * WhatsApp number depend on the country).
 *
 * @returns i18n key if invalid, undefined if valid — or if the field has no rule
 */
export function validatePatientField(
    field: string,
    data: Partial<PatientValidationData>
): string | undefined {
    switch (field) {
        case "first_name":
            return validateName(data.first_name ?? "");
        case "last_name":
            return validateLastName(data.last_name ?? "");
        case "contact_email":
            return validateEmail(data.contact_email ?? "");
        case "contact_phone":
            return validatePhone(data.contact_phone ?? "");
        case "date_of_birth":
            return data.date_of_birth ? validateDateOfBirth(data.date_of_birth) : undefined;
        case "whatsapp_phone":
            return validateWhatsAppNumber(data.whatsapp_phone, data.country);
        case "postal_code":
            return data.postal_code && data.country
                ? validatePostalCode(data.postal_code, data.country)
                : undefined;
        default:
            return undefined;
    }
}

/**
 * Validates patient form data and returns a map of field → i18n error key.
 * An empty object means the data is valid.
 */
export function validatePatient(
    data: Partial<PatientValidationData>
): Record<string, string> {
    const errors: Record<string, string> = {};
    for (const field of VALIDATED_PATIENT_FIELDS) {
        const error = validatePatientField(field, data);
        if (error) errors[field] = error;
    }
    return errors;
}
